import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

/**
 * Renders AI / LLM output as formatted markdown — headings, lists, bold,
 * inline + block code, and GFM tables (which finance answers lean on).
 * Styled inline with the app's ink-* tokens so we don't need the Tailwind
 * typography plugin, and so it reads well in light + dark mode.
 */

const components: Components = {
  h1: ({ children }) => <h1 className="text-base font-semibold mt-3 mb-1.5 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mt-2.5 mb-1 first:mt-0">{children}</h3>,
  p: ({ children }) => <p className="my-1.5 leading-relaxed first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 ml-5 list-disc space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 ml-5 list-decimal space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-ink-900 dark:text-ink-100">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:underline">
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-ink-300 dark:border-ink-700 pl-3 text-ink-600 dark:text-ink-300">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const isBlock = /language-/.test(className ?? '');
    if (isBlock) {
      return (
        <code className="block my-2 p-3 rounded-lg bg-ink-100 dark:bg-ink-950 text-[12px] font-mono overflow-x-auto whitespace-pre">
          {children}
        </code>
      );
    }
    return <code className="px-1 py-0.5 rounded bg-ink-100 dark:bg-ink-800 text-[0.85em] font-mono">{children}</code>;
  },
  pre: ({ children }) => <pre className="my-0">{children}</pre>,
  hr: () => <hr className="my-3 border-ink-200 dark:border-ink-800" />,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full text-xs border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-ink-100 dark:bg-ink-900">{children}</thead>,
  th: ({ children }) => <th className="border border-ink-200 dark:border-ink-800 px-2 py-1 text-left font-medium">{children}</th>,
  td: ({ children }) => <td className="border border-ink-200 dark:border-ink-800 px-2 py-1 align-top">{children}</td>,
};

export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={`text-sm text-ink-700 dark:text-ink-200 ${className ?? ''}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
